<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="saveLanguage">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ isEdit ? $t('CMS-Sprache bearbeiten') : $t('CMS-Sprache anlegen') }}</h1>
                    </div>
                    <div class="inline-flex items-center">
                        <div
                            v-if="isEdit"
                            ref="headerActionMenuWrapper"
                            class="relative inline-flex items-stretch mr-0"
                        >
                            <button class="btn-stage-primary !rounded-r-none !pr-3.5 inline-flex items-center gap-1.5" type="submit">
                                <span>{{ $t('Sprache speichern') }}</span>
                            </button>
                            <button
                                ref="headerActionMenuTrigger"
                                class="btn-stage-primary !rounded-l-none border-l border-l-[rgba(255,255,255,0.16)] min-w-[2.25rem] !px-2 inline-flex items-center justify-center"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :aria-expanded="headerActionMenuOpen ? 'true' : 'false'"
                                @click.stop="toggleHeaderActionMenu"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                            <div
                                v-show="headerActionMenuOpen"
                                ref="headerActionMenu"
                                class="absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                            >
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('cancel')">
                                    {{ $t('Abbrechen') }}
                                </button>
                                <button v-if="language.is_default === false" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('delete')">
                                    {{ $t('Sprache löschen') }}
                                </button>
                            </div>
                        </div>
                        <button v-else class="btn-stage-primary" type="submit">
                            {{ $t('Sprache anlegen') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <app-panel :title="$t('Allgemein')">
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <label class="stage-filter-label block mb-2">{{ $t('Name') }}</label>
                                <input v-model="language.name" class="stage-filter-input form-input block w-full text-sm" required>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2">{{ $t('Locale') }}</label>
                                <input v-model="language.locale" class="stage-filter-input form-input block w-full text-sm" required placeholder="de">
                            </div>
                        </div>

                        <div class="mt-6 flex flex-col gap-6 xl:flex-row xl:items-center">
                            <div v-if="language.is_default" class="flex-1 min-w-0 text-sm text-gray-500">
                                {{ $t('Basissprache des CMS · nicht löschbar') }}
                            </div>
                            <div class="shrink-0 flex items-center">
                                <input-switch
                                    id="cms-language-active"
                                    v-model="language.is_active"
                                    :disabled="language.is_default"
                                    :disabled-label="$t('Aktiviert')"
                                    :enabled-label="$t('Aktiviert')"
                                />
                            </div>
                        </div>
                </app-panel>
            </div>
        </form>
        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :headline-id="'cms-language-confirm-modal-headline'"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />
    </main>
</template>

<script>
import ConfirmDialog from "@/components/modals/confirm-dialog";

export default {
    name: "cms-locale-editor",
    components: {
        ConfirmDialog,
    },
    data() {
        return {
            loading: false,
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            },
            headerActionMenuOpen: false,
            language: {
                locale: 'de',
                name: '',
                is_active: true,
                is_default: false,
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        if (this.isEdit) {
            this.getLanguage();
        }
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        closeHeaderActionMenu() {
            this.headerActionMenuOpen = false;
        },
        toggleHeaderActionMenu() {
            this.headerActionMenuOpen = !this.headerActionMenuOpen;
        },
        runHeaderAction(action) {
            this.closeHeaderActionMenu();

            if (action === 'cancel') {
                this.$router.push('/workspace/cms/languages');
                return;
            }

            if (action === 'delete') {
                this.deleteLanguage();
            }
        },
        handleDocumentClick(event) {
            if (!this.headerActionMenuOpen) {
                return;
            }

            const menuElement = this.$refs.headerActionMenu;
            const triggerElement = this.$refs.headerActionMenuTrigger;
            const wrapperElement = this.$refs.headerActionMenuWrapper;

            if (!(menuElement && menuElement.contains(event.target))
                && !(triggerElement && triggerElement.contains(event.target))
                && !(wrapperElement && wrapperElement.contains(event.target))) {
                this.closeHeaderActionMenu();
            }
        },
        getLanguage() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/languages/' + this.$route.params.id).then((response) => {
                this.language = response.data;
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        saveLanguage() {
            const request = this.isEdit
                ? axios.put('api/workspace/admin/cms/languages/' + this.$route.params.id, this.language)
                : axios.post('api/workspace/admin/cms/languages', this.language);

            request.then((response) => {
                this.$notify({title: this.$t('Success'), text: this.isEdit ? this.$t('Data updated correctly') : this.$t('Data saved correctly'), type: 'success'});
                if (!this.isEdit) {
                    this.$router.push('/workspace/cms/languages/' + response.data.language.id + '/edit');
                } else {
                    this.language = response.data.language;
                }
            });
        },
        deleteLanguage() {
            this.openConfirmDialog({
                title: this.$t('Sprache löschen'),
                text: this.$t('Soll diese CMS-Sprache wirklich gelöscht werden?'),
                pendingAction: () => {
                    axios.delete('api/workspace/admin/cms/languages/' + this.$route.params.id).then(() => {
                        this.$notify({title: this.$t('Success'), text: this.$t('Data deleted successfully'), type: 'success'});
                        this.$router.push('/workspace/cms/languages');
                    });
                },
            });
        },
        openConfirmDialog({title, text, pendingAction}) {
            this.confirmDialog = {
                open: true,
                title,
                text,
                pendingAction,
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
    },
}
</script>
